import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Divider,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  alpha,
  styled
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FolderOpen as FolderIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  History as HistoryIcon,
  Clear as ClearIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import useAuthStore from '../../stores/authStore';
import { getProjects } from '../../services/projectService';
import { useTheme } from '@mui/material/styles';

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: theme.spacing(1),
    minWidth: 320,
    maxHeight: 500,
    boxShadow: theme.shadows[8],
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    '& .MuiList-root': {
      padding: 0,
    },
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

const ProjectButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.primary,
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}));

const ProjectSwitcher = () => {
  const navigate = useNavigate();
  const { id: currentProjectId } = useParams();
  const { user } = useAuthStore();
  const theme = useTheme();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  
  const open = Boolean(anchorEl);
  
  // Listen for keyboard shortcut to open
  useEffect(() => {
    const handleOpenShortcut = () => {
      const button = document.querySelector('[data-project-switcher-button]');
      if (button) button.click();
    };
    
    window.addEventListener('openProjectSwitcher', handleOpenShortcut);
    return () => window.removeEventListener('openProjectSwitcher', handleOpenShortcut);
  }, []);
  
  // Local storage key for user-specific data
  const storageKey = `projectSwitcher_${user?.id}`;
  
  // Get user-specific data from localStorage
  const getUserData = () => {
    try {
      const data = localStorage.getItem(storageKey);
      return data ? JSON.parse(data) : { recent: [], favorites: [] };
    } catch (error) {
      console.error('Error reading user project data:', error);
      return { recent: [], favorites: [] };
    }
  };
  
  // Save user-specific data to localStorage
  const saveUserData = (data) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving user project data:', error);
    }
  };
  
  // Add project to recent list
  const addToRecent = (projectId) => {
    const userData = getUserData();
    const recent = userData.recent.filter(id => id !== projectId);
    recent.unshift(projectId);
    userData.recent = recent.slice(0, 5); // Keep only 5 most recent
    saveUserData(userData);
  };
  
  // Toggle favorite status
  const toggleFavorite = (projectId, event) => {
    event.stopPropagation();
    const userData = getUserData();
    const favorites = userData.favorites || [];
    
    if (favorites.includes(projectId)) {
      userData.favorites = favorites.filter(id => id !== projectId);
    } else {
      userData.favorites = [...favorites, projectId];
    }
    
    saveUserData(userData);
    // Force re-render
    setProjects([...projects]);
  };
  
  // Fetch current project details
  useEffect(() => {
    if (currentProjectId && projects.length > 0) {
      const project = projects.find(p => p._id === currentProjectId);
      setCurrentProject(project);
      if (project) {
        addToRecent(currentProjectId);
      }
    }
  }, [currentProjectId, projects]);
  
  // Fetch all projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await getProjects({ limit: 100 });
        console.log('ProjectSwitcher API response:', response);
        console.log('Projects data:', response.data);
        console.log('Full response structure:', JSON.stringify(response, null, 2));
        
        // Try different possible response structures
        const projectsData = response.data?.projects || response.projects || response.data || [];
        console.log('Extracted projects:', projectsData);
        
        setProjects(Array.isArray(projectsData) ? projectsData : []);
      } catch (error) {
        console.error('Error fetching projects:', error);
        console.error('Error details:', error.response || error);
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id) {
      fetchProjects();
    }
  }, [user?.id]);
  
  // Filter and categorize projects
  const categorizedProjects = useMemo(() => {
    const userData = getUserData();
    console.log('Current projects:', projects);
    console.log('Projects length:', projects.length);
    
    const filtered = projects.filter(project =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.client?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    const favorites = filtered.filter(p => userData.favorites.includes(p._id));
    const recent = filtered
      .filter(p => userData.recent.includes(p._id) && !userData.favorites.includes(p._id))
      .sort((a, b) => userData.recent.indexOf(a._id) - userData.recent.indexOf(b._id));
    const others = filtered.filter(
      p => !userData.favorites.includes(p._id) && !userData.recent.includes(p._id)
    );
    
    return { favorites, recent, others };
  }, [projects, searchQuery]);
  
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };
  
  const handleProjectSelect = (projectId) => {
    handleClose();
    navigate(`/projects/${projectId}/kanban`);
  };
  
  const getProjectStatusColor = (status) => {
    const statusColors = {
      active: 'success',
      completed: 'default',
      on_hold: 'warning',
      cancelled: 'error'
    };
    return statusColors[status] || 'default';
  };
  
  const renderProjectItem = (project, showFavorite = true) => {
    const userData = getUserData();
    const isFavorite = userData.favorites.includes(project._id);
    const isCurrentProject = project._id === currentProjectId;
    
    return (
      <MenuItem
        key={project._id}
        onClick={() => handleProjectSelect(project._id)}
        selected={isCurrentProject}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <FolderIcon color={isCurrentProject ? 'primary' : 'action'} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: isCurrentProject ? 600 : 400 }}>
                {project.name}
              </Typography>
              <Chip
                label={project.status}
                size="small"
                color={getProjectStatusColor(project.status)}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          }
          secondary={
            project.client && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {project.client.name}
                </Typography>
              </Box>
            )
          }
        />
        {showFavorite && (
          <IconButton
            size="small"
            onClick={(e) => toggleFavorite(project._id, e)}
            sx={{ ml: 1 }}
          >
            {isFavorite ? (
              <StarIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
            ) : (
              <StarBorderIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        )}
      </MenuItem>
    );
  };
  
  return (
    <>
      <Tooltip title="Switch project (Cmd/Ctrl + P)">
        <ProjectButton
          onClick={handleOpen}
          endIcon={<ExpandMoreIcon />}
          data-project-switcher-button
          sx={{
            ml: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          {currentProject ? (
            <>
              <FolderIcon sx={{ mr: 1, fontSize: 20 }} />
              {currentProject.name}
            </>
          ) : (
            'Select Project'
          )}
        </ProjectButton>
      </Tooltip>
      
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <SearchField
            fullWidth
            size="small"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <Divider />
        
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {categorizedProjects.favorites.length > 0 && (
                <>
                  <SectionHeader>
                    <StarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Favorites
                  </SectionHeader>
                  {categorizedProjects.favorites.map(project => renderProjectItem(project))}
                </>
              )}
              
              {categorizedProjects.recent.length > 0 && (
                <>
                  {categorizedProjects.favorites.length > 0 && <Divider sx={{ my: 1 }} />}
                  <SectionHeader>
                    <HistoryIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Recent
                    <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                      (Cmd/Ctrl + 1-5)
                    </Typography>
                  </SectionHeader>
                  {categorizedProjects.recent.map((project, index) => (
                    <Box key={project._id} sx={{ position: 'relative' }}>
                      {renderProjectItem(project)}
                      {index < 5 && (
                        <Chip
                          label={`⌘${index + 1}`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            right: 48,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: 20,
                            fontSize: '0.7rem',
                            opacity: 0.5,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </>
              )}
              
              {categorizedProjects.others.length > 0 && (
                <>
                  {(categorizedProjects.favorites.length > 0 || categorizedProjects.recent.length > 0) && (
                    <Divider sx={{ my: 1 }} />
                  )}
                  <SectionHeader>All Projects</SectionHeader>
                  {categorizedProjects.others.map(project => renderProjectItem(project))}
                </>
              )}
              
              {projects.length === 0 && !loading && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 3, textAlign: 'center' }}
                >
                  No projects found
                </Typography>
              )}
            </>
          )}
        </Box>
      </StyledMenu>
    </>
  );
};

export default ProjectSwitcher;